import { Trans } from 'react-i18next'

import {
  COND_FEATURE_FLAG_ALL,
  ConditionBooleanOperator,
  ConditionExpression,
  ConditionFeatureFlag,
  ConditionNumericOperator,
  ConditionStatementCustomText,
  ConditionTextOperator,
  ConditionValue,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_INPUT_RHS,
} from '@makyoapp/makyo-common'
import { Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, SelectDropdown } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey } from '@/shared/builder/types'
import { getCurrentFormFieldColumns } from '@/shared/form-utils'
import { filterComponentInBody } from '@/utils/builder/app-builder'

import { RepeaterButton } from '../RepeaterButton'
import { ConditionOperatorSelectV2 } from './ConditionOperatorSelectV2'
import { ConditionRepeaterContext, useConditionRepeaterContext } from './ConditionRepeaterContext'
import { ConditionSelectV2 } from './ConditionSelectV2'

function ConditionItem({ index }: { index: number }) {
  const { id, onChange, value } = useConditionRepeaterContext()
  const cond = value.conditions[index]

  const handleOperatorChange = (opt: DefaultConditionOperator) => {
    const nextCond = [...value.conditions]
    const isSame = cond.operator === opt
    const needRhs = OPERATOR_WITH_INPUT_RHS.includes(opt)
    const isRange = NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(opt as ConditionNumericOperator)
    const isDate = DATE_OPERATOR_WITH_RHS.includes(opt)
    const isDateRange = DATE_OPERATOR_WITH_RANGE_RHS.includes(opt)

    const nextRhs = isSame
      ? nextCond[index].rhs
      : isDate
        ? ({
            flag: ConditionFeatureFlag.CustomText,
            value: new Date().toISOString(),
          } as ConditionStatementCustomText)
        : isDateRange
          ? ({
              flag: ConditionFeatureFlag.CustomText,
              value: new Date().toISOString(),
              endValue: new Date().toISOString(),
            } as ConditionStatementCustomText)
          : ({
              flag: ConditionFeatureFlag.CustomText,
              // @ts-ignore
              value: cond.rhs?.value,
              // @ts-ignore
              endValue: isRange ? cond.rhs?.endValue : undefined,
            } as ConditionStatementCustomText)

    nextCond[index] = {
      ...nextCond[index],
      operator: opt,
      rhs: nextRhs,
    }

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }

  const handleRemoveCondition = () => {
    const nextCond = [...value.conditions]
    nextCond.splice(index, 1)

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }

  return (
    <div className='flex-c p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700'>
      <div className='flex flex-col gap-2 w-[calc(100%-48px)]'>
        <div className='flex-c w-full'>
          <ConditionSelectV2 id={id ? `${id}-${index}-lhs` : undefined} index={index} />
        </div>

        <ConditionOperatorSelectV2
          id={id ? `${id}-${index}-op` : undefined}
          index={index}
          selected={cond.operator}
          value={value}
          onChange={handleOperatorChange}
        />

        {/* single value (all) */}
        {OPERATOR_WITH_INPUT_RHS.includes(cond.operator) && (
          <ConditionSelectV2 id={id ? `${id}-${index}-rhs` : undefined} index={index} isRhs={true} />
        )}

        {/* single value (date) */}
        {DATE_OPERATOR_WITH_RHS.includes(cond.operator) && (
          <ConditionSelectV2 id={id ? `${id}-${index}-rhs-date` : undefined} isRhs={true} index={index} isDate={true} />
        )}

        {/* range */}
        {[...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(cond.operator) && (
          <div className='mt-1 flex flex-col gap-4'>
            <div className='space-y-1'>
              <div className='uppercase text-m-ss'>
                <Trans>Lower Bound</Trans>
              </div>
              <ConditionSelectV2
                key={`${id}-${index}-rhs-start`}
                id={id ? `${id}-${index}-rhs-start` : undefined}
                isRhs={true}
                index={index}
                isDate={true}
              />
            </div>
            <div className='space-y-1'>
              <div className='uppercase text-m-ss'>
                <Trans>Upper Bound</Trans>
              </div>
              <ConditionSelectV2
                key={`${id}-${index}-rhs-end`}
                id={id ? `${id}-${index}-rhs-end` : undefined}
                isRhs={true}
                index={index}
                isEnd
                isDate={true}
              />
            </div>
          </div>
        )}
      </div>
      <Button variant={ButtonVariant.Ghost} icon={<Trash16 />} onClick={handleRemoveCondition} squared noPadding />
    </div>
  )
}

interface ConditionRepeaterProps {
  id?: string
  value: ConditionValue
  onChange?: (nextValue: ConditionValue) => void
  featureFlags?: ConditionFeatureFlag
  parentFormId?: string
}

export function ConditionRepeaterV2({
  id,
  value = {
    isAny: false,
    conditions: [],
  },
  onChange,
  featureFlags = COND_FEATURE_FLAG_ALL,
  parentFormId,
}: ConditionRepeaterProps) {
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()

  const getDefaultCondition = (): ConditionExpression[] => {
    // form elements: generate default form condition
    if (parentFormId) {
      const containerSettings = activeLayerClient?.settings._BodySettings
      const form = activeLayerClient?.componentsMap.get(parentFormId as string)

      if (form) {
        const formChildren = filterComponentInBody(form._Body, (el) => ['form', 'upload'].includes(el._GroupID))
        const formSettings = form?.settings
        const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
        const table = tables.find((t) => t.id === dataSource)

        if (containerSettings && formChildren && table) {
          const columns = getCurrentFormFieldColumns(containerSettings, formChildren)
          const firstColId = columns[0]

          return [
            {
              lhs: { flag: ConditionFeatureFlag.FormData, formId: parentFormId, col: firstColId },
              operator: ConditionTextOperator.IsNotEmpty,
            },
          ]
        }
      }
    }

    // other elements: generate default user logged in condition
    return [
      {
        lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
        operator: ConditionBooleanOperator.IsFalse,
      },
    ]
  }

  const handleAddCondition = () => {
    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: [...(value.conditions || []), ...getDefaultCondition()],
      })
    }
  }

  return (
    <ConditionRepeaterContext.Provider value={{ id, value, onChange, featureFlags, parentFormId }}>
      <div id={id} className='flex flex-col gap-4 w-full'>
        <div className='flex-c'>
          <div>
            <Trans>If</Trans>
          </div>
          <SelectDropdown
            id={id ? `${id}-any` : undefined}
            value={value ? (value?.isAny ? 'any' : 'all') : 'any'}
            onChange={(opt) => {
              onChange?.({
                isAny: opt === 'any',
                conditions: value?.conditions ?? [],
              })
            }}
            renderOption={(opt) => (
              <div className='uppercase'>
                <Trans>{opt}</Trans>
              </div>
            )}
            options={['any', 'all']}
          />
          <div>
            <Trans>of the following conditions are met</Trans>
          </div>
        </div>
        {value && value.conditions?.length > 0 && (
          <div className='flex gap-2 flex-col w-full min-w-0'>
            {value.conditions.map((_, i) => {
              return <ConditionItem index={i} key={i} />
            })}
          </div>
        )}
        <RepeaterButton id={id ? `${id}-repeat` : undefined} onClick={handleAddCondition}>
          <Trans>Add Condition</Trans>
        </RepeaterButton>
      </div>
    </ConditionRepeaterContext.Provider>
  )
}
